import React, { useMemo } from "react";

import { useParams } from "react-router-dom";

import type { UserID } from "../../../../../hooks/useAccount";
import { useProject } from "../../../../../hooks/useProject";
import {
  type ProjectMember,
  ProjectUserAssignableRoles,
  ProjectUserRoleDisplayName,
  useUpdateProjectUser,
} from "../../../../../hooks/useProjectSharing";
import { useTeamActiveSubscription } from "../../../../../hooks/useTeamActiveSubscription";
import { useTeam } from "../../../../../hooks/useTeams";
import { useTracker } from "../../../../../hooks/useTracker";
import { ShareRow, type ShareRowRef } from "./ShareRow";

interface MemberRowProps {
  member: ProjectMember;
  onRemove?: (userId: UserID) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  showPaywall?: () => void;
  isCurrentUser?: boolean;
}

export const MemberRowProject = React.forwardRef<ShareRowRef, MemberRowProps>(
  ({ member, onRemove, containerIntersectionObserver, showPaywall, isCurrentUser }, ref) => {
    const tracker = useTracker();
    const { projectId = "" } = useParams();

    const { data: projectData } = useProject();
    const { teamData } = useTeam(projectData?.teamId);

    const {
      mutateAsync: mutateUpdateProjectUser,
      isPending: isUpdatePending,
      isError: isUpdateError,
    } = useUpdateProjectUser({});

    const availableRoles = useMemo(() => {
      if (isCurrentUser) {
        return [member.role] as const;
      }
      return ProjectUserAssignableRoles;
    }, [isCurrentUser, member.role]);

    const { data: isTeamSubscribed, isPending: isSubscriptionDataPending } =
      useTeamActiveSubscription(projectData?.teamId);

    return (
      <ShareRow
        ref={ref}
        roles={availableRoles}
        roleDisplayName={ProjectUserRoleDisplayName}
        entity={member}
        proRoles={new Set<(typeof ProjectUserAssignableRoles)[number]>(["editor"])}
        showPaywall={showPaywall}
        isProRoleEligible={isTeamSubscribed ?? false}
        containerIntersectionObserver={containerIntersectionObserver}
        onUpdate={(prevRole, newRole) => {
          tracker.trackShareModalMemberRoleChanged({
            remoteProjectId: projectId,
            affectedUserId: member.userId,
            oldValue: prevRole,
            newValue: newRole,
            sharedResource: "project",
            spaceId: projectData?.space?.spaceId,
            teamUserRole: teamData?.role,
            projectUserRole: projectData?.userRole,
          });
          return mutateUpdateProjectUser({
            userId: member.userId,
            role: newRole,
          });
        }}
        onRemove={() => {
          onRemove?.(member.userId);
          tracker.trackShareModalMemberRemoveIntended({
            remoteProjectId: projectId,
            affectedUserId: member.userId,
            sharedResource: "project",
            spaceId: projectData?.space?.spaceId,
            teamUserRole: teamData?.role,
            projectUserRole: projectData?.userRole,
          });
        }}
        isPending={isUpdatePending || isSubscriptionDataPending}
        isError={isUpdateError}
        sharingBadges={[
          ...(member.isTeamUser ? [] : ["Guest" as const]),
          ...(isCurrentUser ? ["You" as const] : []),
        ]}
        removeActionText={isCurrentUser ? "Leave..." : "Remove..."}
      />
    );
  }
);
